import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Plus } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";
import {
  TravelRequestCard,
  TravelRequestsTable,
  SortTravelRequestButton,
  ViewTravelRequestButton,
  FilterTravelRequestButton,
  CreateClientRequestModal,
  type SortMode,
  type ViewMode,
} from "@/lib/requests";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { FilterConfig, FilterOption } from "@/components/ui/filter-dropdown";
import { RequestsTabProps } from "./types";

const filterOptions: FilterOption[] = [
  { value: 'destinations', label: 'Destinations', operations: [{ value: 'contains', label: 'Contains' }] },
  { value: 'customer_name', label: 'Customer Name', operations: [{ value: 'contains', label: 'Contains' }] },
  {
    value: 'budget', label: 'Budget',
    operations: [
      { value: 'greater_than', label: '>' }, { value: 'less_than', label: '<' },
      { value: 'greater_equal', label: '>=' }, { value: 'less_equal', label: '<=' }
    ]
  },
  {
    value: 'guests', label: 'Guests',
    operations: [
      { value: 'equals', label: '=' }, { value: 'greater_than', label: '>' },
      { value: 'less_than', label: '<' }, { value: 'greater_equal', label: '>=' },
      { value: 'less_equal', label: '<=' }
    ]
  },
  { value: 'departure', label: 'Departure', operations: [{ value: 'contains', label: 'Contains' }] },
  { value: 'travel_start_date', label: 'Travel Start Date', is_date: true, operations: [] },
  { value: 'travel_end_date', label: 'Travel End Date', is_date: true, operations: [] },
  { value: 'created_at', label: 'Created Date', is_date: true, operations: [] },
];

export const DashboardRequestsTab = ({
  userRole,
  travelRequests,
  loading,
  error,
  onRefresh,
  refreshing,
  onViewRequest,
  onCreateItinerary,
}: RequestsTabProps) => {
  const { t } = useI18n();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [sortedTravelRequests, setSortedTravelRequests] = useState<DashboardTravelRequest[]>([]);
  const [displayedTravelRequests, setDisplayedTravelRequests] = useState<DashboardTravelRequest[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    if (travelRequests.length > 0) {
      const sorted = [...travelRequests].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setSortedTravelRequests(sorted);
      setDisplayedTravelRequests(sorted);
    } else {
      setSortedTravelRequests([]);
      setDisplayedTravelRequests([]);
    }
  }, [travelRequests]);

  // Se filtri/ordinamento accorciano la lista, la pagina corrente può non
  // esistere più: la tabella risulterebbe vuota senza paginatore.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(displayedTravelRequests.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(1);
  }, [displayedTravelRequests, currentPage]);

  const newRequests = displayedTravelRequests.filter(req => {
    const diffInHours = (Date.now() - new Date(req.createdAt).getTime()) / (1000 * 60 * 60);
    return diffInHours < 24;
  }).length;

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'grid') setCurrentPage(1);
  };

  const handleCreateSuccess = () => {
    onRefresh();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
        <div className="flex items-center gap-2">
          <h6 className="text-gray-500">
            {travelRequests.length
              ? t('dashboard.requestsCount', { new: newRequests, total: travelRequests.length })
              : t('dashboard.noRequests')}
          </h6>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-[#030A14] hover:bg-[#030A14]/90"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nuova Richiesta</span>
          </Button>
          <FilterTravelRequestButton
            filters={filters}
            onFiltersChange={setFilters}
            options={filterOptions}
            travelRequests={sortedTravelRequests}
            onFilteredTravelRequestsChange={(r) => setDisplayedTravelRequests(r as DashboardTravelRequest[])}
            disabled={travelRequests.length === 0}
          />
          <SortTravelRequestButton
            sortMode={sortMode}
            onSortModeChange={setSortMode}
            travelRequests={travelRequests}
            onSortedTravelRequestsChange={(r) => setSortedTravelRequests(r as DashboardTravelRequest[])}
            filters={filters}
            onFilteredTravelRequestsChange={(r) => setDisplayedTravelRequests(r as DashboardTravelRequest[])}
            disabled={travelRequests.length === 0}
          />
          <ViewTravelRequestButton
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            disabled={travelRequests.length === 0}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {displayedTravelRequests.map((travelRequest, index) => (
              <div
                key={travelRequest.id}
                className="animate-in fade-in zoom-in-95 duration-500"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
              >
                <TravelRequestCard
                  travelRequest={travelRequest}
                  userRole={userRole}
                  onViewRequest={() => onViewRequest(travelRequest)}
                  onCreateItinerary={() => onCreateItinerary(travelRequest)}
                />
              </div>
            ))}
          </div>
        ) : (
          <TravelRequestsTable
            travelRequest={displayedTravelRequests}
            currentPage={currentPage}
            userRole={userRole}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            hideActions
            onRowClick={(id) => {
              const req = displayedTravelRequests.find(r => r.id === id);
              if (req) onViewRequest(req);
            }}
            onViewRequest={(id) => {
              const req = displayedTravelRequests.find(r => r.id === id);
              if (req) onViewRequest(req);
            }}
            onCreateItinerary={(id) => {
              const req = displayedTravelRequests.find(r => r.id === id);
              if (req) onCreateItinerary(req);
            }}
          />
        )}

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-center py-12">
          <h4 className="text-red-600">{t('dashboard.loadError')}</h4>
          <Button onClick={onRefresh} className="mt-4">{t('dashboard.retry')}</Button>
        </div>
      )}

      {/* Empty – no data */}
      {!loading && !error && travelRequests.length === 0 && (
        <div className="text-center py-12">
          <h4 className="text-gray-600 mb-4">{t('dashboard.noAssignedTrips')}</h4>
          <p className="text-gray-500 mb-4">{t('dashboard.noAssignedTripsDesc')}</p>
        </div>
      )}

      {/* Empty – filtered away */}
      {!loading && !error && travelRequests.length > 0 && displayedTravelRequests.length === 0 && (
        <div className="text-center py-12">
          <h4 className="text-gray-600 mb-4">{t('dashboard.noSearchResults')}</h4>
          <p className="text-gray-500 mb-4">{t('dashboard.noSearchResultsDesc')}</p>
          <Button
            variant="outline"
            onClick={() => {
              setFilters([]);
              setDisplayedTravelRequests(sortedTravelRequests);
            }}
          >
            {t('dashboard.removeAllFilters')}
          </Button>
        </div>
      )}

      {/* Create Client Request Modal */}
      <CreateClientRequestModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};
